import { createEvent, createStore, sample, Event, Store } from "effector";
import { routeStateChanged } from "@features/app/model";
import { parseQueryParams } from "@features/helpers";

export type RouteState = {
    path: string;
    params: Record<string, string | string[]>;
    search: string;
};

export type RouteSnapshot = RouteState & {
    query: Record<string, string>;
    __routeKey: string;
};

type RouteMatcher = (state: RouteState) => boolean;

type RouteConfig =
    | {
          path: string;
          exact?: boolean;
      }
    | {
          regex: RegExp;
      }
    | {
          match: RouteMatcher;
      };

export type RouteUnit = {
    opened: Event<RouteSnapshot>;
    changed: Event<RouteSnapshot>;
    closed: Event<RouteSnapshot>;
    $state: Store<RouteSnapshot | null>;
    $isActive: Store<boolean>;
    $path: Store<string | null>;
    $params: Store<Record<string, string | string[]>>;
    $search: Store<string>;
    $query: Store<Record<string, string>>;
};

/**
 * Normalizes any supported configuration into a predicate against `RouteState`.
 */
const toMatcher = (config: RouteConfig | RouteMatcher | string | RegExp): RouteMatcher => {
    if (typeof config === "function") {
        return config;
    }

    if (typeof config === "string") {
        return (state) => state.path.startsWith(config);
    }

    if (config instanceof RegExp) {
        return (state) => config.test(state.path);
    }

    if ("match" in config) {
        return config.match;
    }

    if ("regex" in config) {
        return (state) => config.regex.test(state.path);
    }

    return config.exact ? (state) => state.path === config.path : (state) => state.path.startsWith(config.path);
};

/**
 * Extracts a stable route identity key.
 * For prefix routes this is the base path (e.g. "/prompts-library").
 */
const getRouteKey = (config: RouteConfig | RouteMatcher | string | RegExp): string => {
    if (typeof config === "string") return config;
    if (typeof config === "function") return "custom";
    if (config instanceof RegExp) return config.source;
    if ("path" in config) return config.path;
    if ("regex" in config) return config.regex.source;
    return "unknown";
};

/**
 * Extends raw route state with parsed query and route identity.
 */
const enrichSnapshot = (state: RouteState, routeKey: string): RouteSnapshot => ({
    ...state,
    query: parseQueryParams(state.search ?? ""),
    __routeKey: routeKey
});

/**
 * Creates an isolated route unit with stable lifecycle semantics
 * suitable for Next.js App Router.
 */
export const createRouteUnit = (config: RouteConfig | RouteMatcher | string | RegExp): RouteUnit => {
    const matcher = toMatcher(config);
    const routeKey = getRouteKey(config);

    const opened = createEvent<RouteSnapshot>();
    const changed = createEvent<RouteSnapshot>();
    const closed = createEvent<RouteSnapshot>();

    const applyState = createEvent<{ current: RouteSnapshot | null; next: RouteSnapshot }>();
    const resetState = createEvent<RouteSnapshot>();

    const $state = createStore<RouteSnapshot | null>(null)
        .on(applyState, (_, { next }) => next)
        .reset(resetState);

    const $isActive = $state.map(Boolean);
    const $path = $state.map((state) => state?.path ?? null);
    const $params = $state.map((state) => state?.params ?? {});
    const $search = $state.map((state) => state?.search ?? "");
    const $query = $state.map((state) => state?.query ?? {});

    const routeEvaluated = createEvent<{
        current: RouteSnapshot | null;
        next: RouteSnapshot;
        matched: boolean;
    }>();

    sample({
        clock: routeStateChanged,
        source: $state,
        fn: (current, payload) => ({
            current,
            next: enrichSnapshot(payload, routeKey),
            matched: matcher(payload)
        }),
        target: routeEvaluated
    });
    sample({
        clock: routeEvaluated,
        filter: ({ matched }) => matched,
        fn: ({ current, next }) => ({ current, next }),
        target: applyState
    });

    sample({
        clock: routeEvaluated,
        source: $state,
        filter: (current, { matched }) => Boolean(current) && matched === false,
        fn: (current) => current!,
        target: resetState
    });

    sample({
        clock: applyState,
        filter: ({ current }) => current === null,
        fn: ({ next }) => next,
        target: opened
    });

    sample({
        clock: applyState,
        filter: ({ current }) => current !== null,
        fn: ({ next }) => next,
        target: changed
    });

    sample({
        clock: resetState,
        target: closed
    });

    return {
        opened,
        changed,
        closed,
        $state,
        $isActive,
        $path,
        $params,
        $search,
        $query
    };
};
